import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import CurrencyInput from '../components/CurrencyInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatMonth, getStatusLabel, getPaymentTypeLabel, getMonthOptions, getCurrentMonth } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  CheckCircle, 
  Trash2, 
  CreditCard,
  Filter,
  AlertCircle,
  Printer,
  Calendar,
  Landmark,
  Copy,
  Check,
  Upload,
  Image,
  X,
  Eye,
  XCircle,
  Clock
} from 'lucide-react';
import Receipt from '../components/Receipt';
import { useRef } from 'react';
import { MobileCardContainer, MobileCard, MobileCardHeader, MobileCardTitle, MobileCardContent, MobileCardRow, MobileCardFooter } from '../components/MobileCard';

import Pagination from '../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ITEMS_PER_PAGE = 10;

const periodOptions = [
  { value: 'all', label: 'Semua Periode' },
  { value: 'this_month', label: 'Bulan Ini' },
  { value: 'last_month', label: 'Bulan Kemarin' },
  { value: 'last_3_months', label: '3 Bulan Terakhir' },
  { value: 'last_6_months', label: '6 Bulan Terakhir' },
  { value: 'custom', label: 'Custom' },
];

const PaymentForm = ({ tenants, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    tenant_id: '',
    amount: '',
    month: getCurrentMonth(),
    payment_type: 'sewa',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Auto-fill amount when tenant is selected for sewa
  useEffect(() => {
    if (formData.tenant_id && formData.payment_type === 'sewa') {
      const tenant = tenants.find(t => t.id === formData.tenant_id);
      if (tenant) {
        // Fetch room price
        axios.get(`${API_URL}/api/rooms/${tenant.room_id}`).then(res => {
          setFormData(prev => ({ ...prev, amount: res.data.price }));
        });
      }
    }
  }, [formData.tenant_id, formData.payment_type, tenants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tenant_id || !formData.amount || !formData.month) {
      toast.error('Mohon isi semua field yang wajib');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/payments`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Tagihan berhasil dibuat');
      onSave();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat tagihan');
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = getMonthOptions(12);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="tenant_id">Penghuni *</Label>
        <Select value={formData.tenant_id} onValueChange={(value) => setFormData(prev => ({ ...prev, tenant_id: value }))}>
          <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="payment-tenant-select">
            <SelectValue placeholder="Pilih penghuni" />
          </SelectTrigger>
          <SelectContent>
            {tenants.filter(t => t.status === 'aktif').map((tenant) => (
              <SelectItem key={tenant.id} value={tenant.id}>
                {tenant.name} - {tenant.room_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="month">Bulan *</Label>
          <Select value={formData.month} onValueChange={(value) => setFormData(prev => ({ ...prev, month: value }))}>
            <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="payment-month-select">
              <SelectValue placeholder="Pilih bulan" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="payment_type">Jenis Pembayaran *</Label>
          <Select value={formData.payment_type} onValueChange={(value) => setFormData(prev => ({ ...prev, payment_type: value }))}>
            <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="payment-type-select">
              <SelectValue placeholder="Pilih jenis" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sewa">Sewa Kamar</SelectItem>
              <SelectItem value="listrik">Listrik</SelectItem>
              <SelectItem value="air">Air</SelectItem>
              <SelectItem value="lainnya">Lainnya</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="amount">Jumlah *</Label>
        <CurrencyInput
          id="amount"
          name="amount"
          placeholder="0"
          value={formData.amount}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="payment-amount-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Keterangan (opsional)</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Keterangan tambahan..."
          value={formData.description}
          onChange={handleChange}
          className="bg-muted border-0 rounded-xl resize-none"
          rows={2}
          data-testid="payment-description-input"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="payment-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : 'Buat Tagihan'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const Pembayaran = () => {
  const { isPemilik } = useAuth();
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const receiptRef = useRef(null);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, belumBayar: 0, totalPaid: 0, totalPending: 0, menungguVerifikasi: 0 });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  
  // Upload proof states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // View proof states
  const [viewProofDialogOpen, setViewProofDialogOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // Calculate date range based on filter
  const getDateRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    switch (periodFilter) {
      case 'all':
        return { startMonth: null, endMonth: null };
      case 'this_month':
        const thisMonth = `${year}-${String(month + 1).padStart(2, '0')}`;
        return { startMonth: thisMonth, endMonth: thisMonth };
      case 'last_month':
        const lastMonth = month === 0 
          ? `${year - 1}-12` 
          : `${year}-${String(month).padStart(2, '0')}`;
        return { startMonth: lastMonth, endMonth: lastMonth };
      case 'last_3_months':
        const start3 = new Date(year, month - 2, 1);
        return { 
          startMonth: `${start3.getFullYear()}-${String(start3.getMonth() + 1).padStart(2, '0')}`,
          endMonth: `${year}-${String(month + 1).padStart(2, '0')}`
        };
      case 'last_6_months':
        const start6 = new Date(year, month - 5, 1);
        return { 
          startMonth: `${start6.getFullYear()}-${String(start6.getMonth() + 1).padStart(2, '0')}`,
          endMonth: `${year}-${String(month + 1).padStart(2, '0')}`
        };
      case 'custom':
        return { 
          startMonth: customRange.start ? customRange.start.substring(0, 7) : null,
          endMonth: customRange.end ? customRange.end.substring(0, 7) : null
        };
      default:
        return { startMonth: null, endMonth: null };
    }
  };

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    setPage(0);
    if (value === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-accounts`);
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const range = getDateRange();
      const params = new URLSearchParams();
      
      if (range.startMonth) params.append('start_month', range.startMonth);
      if (range.endMonth) params.append('end_month', range.endMonth);
      if (filterStatus) params.append('status', filterStatus);
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('skip', (page * ITEMS_PER_PAGE).toString());

      const [paymentsRes, tenantsRes, allPaymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments?${params.toString()}`),
        isPemilik() ? axios.get(`${API_URL}/api/tenants`) : Promise.resolve({ data: { data: [] } }),
        // Get all payments for stats (with same date filter)
        axios.get(`${API_URL}/api/payments?${range.startMonth ? `start_month=${range.startMonth}&` : ''}${range.endMonth ? `end_month=${range.endMonth}&` : ''}limit=1000`)
      ]);
      setPayments(paymentsRes.data.data || []);
      setTotalItems(paymentsRes.data.total || 0);
      setTenants(tenantsRes.data.data || tenantsRes.data || []);
      
      // Calculate stats from all payments
      const allPayments = allPaymentsRes.data.data || [];
      setStats({
        total: allPaymentsRes.data.total || 0,
        belumBayar: allPayments.filter(p => p.status === 'belum_bayar').length,
        menungguVerifikasi: allPayments.filter(p => p.status === 'menunggu_verifikasi').length,
        totalPaid: allPayments.filter(p => p.status === 'lunas').reduce((sum, p) => sum + p.amount, 0),
        totalPending: allPayments.filter(p => p.status !== 'lunas').reduce((sum, p) => sum + p.amount, 0)
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchBankAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, customRange, filterStatus, page]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Nomor rekening disalin');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Handle file selection for proof upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error('Format file tidak didukung. Gunakan JPG, PNG, atau WebP');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }
      setProofFile(file);
      setProofPreview(URL.createObjectURL(file));
    }
  };

  // Upload payment proof
  const handleUploadProof = async () => {
    if (!proofFile || !selectedPayment) return;
    
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      
      await axios.post(`${API_URL}/api/payments/${selectedPayment.id}/upload-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Bukti transfer berhasil diupload. Menunggu verifikasi pemilik.');
      setUploadDialogOpen(false);
      setProofFile(null);
      setProofPreview(null);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengupload bukti transfer');
    } finally {
      setUploadLoading(false);
    }
  };

  // Verify payment (pemilik only)
  const handleVerifyPayment = async () => {
    if (!selectedPayment) return;
    
    setVerifyLoading(true);
    try {
      await axios.post(`${API_URL}/api/payments/${selectedPayment.id}/verify`);
      toast.success('Pembayaran berhasil diverifikasi');
      setViewProofDialogOpen(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal memverifikasi pembayaran');
    } finally {
      setVerifyLoading(false);
    }
  };

  // Reject payment proof (pemilik only)
  const handleRejectProof = async () => {
    if (!selectedPayment) return;
    
    try {
      await axios.post(`${API_URL}/api/payments/${selectedPayment.id}/reject`);
      toast.success('Bukti transfer ditolak');
      setRejectDialogOpen(false);
      setViewProofDialogOpen(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      console.error('Error rejecting proof:', error);
      toast.error(error.response?.data?.detail || 'Gagal menolak bukti transfer');
    }
  };

  const handlePrintReceipt = async (payment) => {
    setReceiptLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/payments/${payment.id}/receipt`);
      setReceiptData(response.data);
      setReceiptDialogOpen(true);
    } catch (error) {
      console.error('Error fetching receipt:', error);
      toast.error('Gagal memuat kwitansi');
    } finally {
      setReceiptLoading(false);
    }
  };

  const printReceipt = () => {
    const printContent = receiptRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Kwitansi Pembayaran - ${receiptData?.receipt_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleMarkAsPaid = (payment) => {
    setSelectedPayment(payment);
    setConfirmDialogOpen(true);
  };

  const confirmMarkAsPaid = async () => {
    try {
      await axios.put(`${API_URL}/api/payments/${selectedPayment.id}`, {
        status: 'lunas'
      });
      toast.success('Pembayaran berhasil dikonfirmasi');
      fetchData();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengonfirmasi pembayaran');
    } finally {
      setConfirmDialogOpen(false);
      setSelectedPayment(null);
    }
  };

  const handleDelete = (payment) => {
    setSelectedPayment(payment);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/payments/${selectedPayment.id}`);
      toast.success('Tagihan berhasil dihapus');
      fetchData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus tagihan');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedPayment(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    fetchData();
  };

  const monthOptions = getMonthOptions(12);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="pembayaran-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pembayaran</h1>
          <p className="text-muted-foreground mt-1">
            {isPemilik() ? 'Kelola tagihan dan pembayaran' : 'Lihat status pembayaran Anda'}
          </p>
        </div>
        {isPemilik() && (
          <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-payment-btn">
            <Plus className="w-5 h-5 mr-2" />
            Buat Tagihan
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <Card className="p-3 md:p-4">
          <p className="text-xl md:text-2xl font-heading font-bold">{stats.total}</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Tagihan</p>
        </Card>
        <Card className="p-3 md:p-4">
          <p className="text-xl md:text-2xl font-heading font-bold text-amber-600">
            {stats.belumBayar}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Belum Bayar</p>
        </Card>
        <Card className="p-3 md:p-4">
          <p className="text-xl md:text-2xl font-heading font-bold text-purple-600">
            {stats.menungguVerifikasi}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground leading-tight">Menunggu Verifikasi</p>
        </Card>
        <Card className="p-3 md:p-4">
          <p className="text-lg md:text-2xl font-heading font-bold text-emerald-600 truncate">
            {formatCurrency(stats.totalPaid)}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Sudah Dibayar</p>
        </Card>
        <Card className="p-3 md:p-4">
          <p className="text-lg md:text-2xl font-heading font-bold text-accent truncate">
            {formatCurrency(stats.totalPending)}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Tertunggak</p>
        </Card>
      </div>

      {/* Bank Account Info - Show to all users */}
      {bankAccounts.length > 0 && (
        <Card className="p-5 border-blue-200 bg-blue-50/50" data-testid="bank-info-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-800">Pembayaran Via Transfer</h3>
              <p className="text-sm text-blue-600">Silakan transfer ke salah satu rekening berikut</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {bankAccounts.map((account) => (
              <div key={account.id} className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="font-medium text-sm text-blue-800">{account.bank_name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm font-mono font-bold">{account.account_number}</code>
                  <button
                    onClick={() => copyToClipboard(account.account_number, account.id)}
                    className="p-1 hover:bg-blue-100 rounded transition-colors"
                    data-testid={`copy-bank-${account.id}`}
                  >
                    {copiedId === account.id ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-1">a.n. {account.account_holder}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={periodFilter} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-[160px] sm:w-[180px]" data-testid="period-filter">
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Pilih Periode" />
          </SelectTrigger>
          <SelectContent>
            {periodOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {periodFilter === 'custom' && (
          <Popover open={showCustomPicker} onOpenChange={setShowCustomPicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                {customRange.start && customRange.end 
                  ? `${customRange.start} - ${customRange.end}`
                  : 'Pilih Tanggal'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tanggal Mulai</Label>
                  <Input
                    type="date"
                    value={customRange.start}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal Akhir</Label>
                  <Input
                    type="date"
                    value={customRange.end}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
                <Button onClick={() => { setShowCustomPicker(false); fetchData(); }} className="w-full">
                  Terapkan
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        <Select value={filterStatus || "all"} onValueChange={(v) => { setFilterStatus(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
            <SelectValue placeholder="Semua Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="belum_bayar">Belum Bayar</SelectItem>
            <SelectItem value="menunggu_verifikasi">Menunggu Verifikasi</SelectItem>
            <SelectItem value="lunas">Lunas</SelectItem>
          </SelectContent>
        </Select>

        {(periodFilter !== 'all' || filterStatus) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPeriodFilter('all');
              setFilterStatus('');
              setPage(0);
            }}
          >
            Reset
          </Button>
        )}
      </div>

      {/* Payment Table */}
      {loading ? (
        <Card className="p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </Card>
      ) : payments.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <CreditCard className="empty-state-icon" />
            <p className="empty-state-title">Belum ada tagihan</p>
            <p className="empty-state-description">
              {isPemilik() ? 'Buat tagihan untuk penghuni' : 'Anda belum memiliki tagihan'}
            </p>
            {isPemilik() && (
              <Button onClick={() => setDialogOpen(true)} className="mt-4 rounded-xl">
                <Plus className="w-5 h-5 mr-2" />
                Buat Tagihan
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <MobileCardContainer>
            {payments.map((payment) => (
              <MobileCard key={payment.id} testId={`payment-card-${payment.id}`}>
                <MobileCardHeader>
                  <MobileCardTitle>
                    <p className="font-semibold">{payment.tenant_name}</p>
                    <p className="text-sm text-muted-foreground">{payment.room_name}</p>
                  </MobileCardTitle>
                  <Badge className={
                    payment.status === 'lunas' ? 'bg-green-100 text-green-700' :
                    payment.status === 'menunggu_verifikasi' ? 'bg-purple-100 text-purple-700' :
                    'bg-amber-100 text-amber-700'
                  }>
                    {payment.status === 'menunggu_verifikasi' && <Clock className="w-3 h-3 mr-1" />}
                    {payment.status === 'lunas' && <CheckCircle className="w-3 h-3 mr-1" />}
                    {getStatusLabel(payment.status)}
                  </Badge>
                </MobileCardHeader>
                <MobileCardContent>
                  <MobileCardRow label="Bulan" value={formatMonth(payment.month)} />
                  <MobileCardRow label="Jenis" value={<Badge variant="outline" className="text-xs">{getPaymentTypeLabel(payment.payment_type)}</Badge>} />
                  <MobileCardRow label="Jumlah" value={formatCurrency(payment.amount)} valueClassName="text-primary font-bold" />
                </MobileCardContent>
                <MobileCardFooter>
                  {payment.proof_image_url ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs"
                      onClick={() => {
                        setSelectedPayment(payment);
                        setViewProofDialogOpen(true);
                      }}
                    >
                      <Eye className="w-3 h-3" />
                      Lihat Bukti
                    </Button>
                  ) : payment.status === 'belum_bayar' && !isPemilik() ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs"
                      onClick={() => {
                        setSelectedPayment(payment);
                        setUploadDialogOpen(true);
                      }}
                    >
                      <Upload className="w-3 h-3" />
                      Upload
                    </Button>
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!isPemilik() && payment.status === 'belum_bayar' && (
                        <DropdownMenuItem onClick={() => { setSelectedPayment(payment); setUploadDialogOpen(true); }}>
                          <Upload className="w-4 h-4 mr-2" />Upload Bukti
                        </DropdownMenuItem>
                      )}
                      {payment.proof_image_url && (
                        <DropdownMenuItem onClick={() => { setSelectedPayment(payment); setViewProofDialogOpen(true); }}>
                          <Eye className="w-4 h-4 mr-2" />Lihat Bukti
                        </DropdownMenuItem>
                      )}
                      {isPemilik() && payment.status === 'menunggu_verifikasi' && (
                        <DropdownMenuItem onClick={() => { setSelectedPayment(payment); setViewProofDialogOpen(true); }} className="text-green-600">
                          <CheckCircle className="w-4 h-4 mr-2" />Verifikasi
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handlePrintReceipt(payment)}>
                        <Printer className="w-4 h-4 mr-2" />Cetak Kwitansi
                      </DropdownMenuItem>
                      {isPemilik() && (
                        <>
                          {payment.status === 'belum_bayar' && (
                            <DropdownMenuItem onClick={() => handleMarkAsPaid(payment)}>
                              <CheckCircle className="w-4 h-4 mr-2" />Konfirmasi Manual
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(payment)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />Hapus
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </MobileCardFooter>
              </MobileCard>
            ))}
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={setPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </MobileCardContainer>

          {/* Desktop Table View */}
          <Card className="overflow-hidden hidden md:block">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Penghuni</TableHead>
                  <TableHead>Bulan</TableHead>
                  <TableHead>Jenis</TableHead>
                  <TableHead>Jumlah</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Bukti Transfer</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{payment.tenant_name}</p>
                        <p className="text-sm text-muted-foreground">{payment.room_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatMonth(payment.month)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{getPaymentTypeLabel(payment.payment_type)}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(payment.amount)}</TableCell>
                    <TableCell>
                      <Badge className={
                        payment.status === 'lunas' ? 'bg-green-100 text-green-700' :
                        payment.status === 'menunggu_verifikasi' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {payment.status === 'menunggu_verifikasi' && <Clock className="w-3 h-3 mr-1" />}
                        {payment.status === 'lunas' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {getStatusLabel(payment.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {payment.proof_image_url ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setViewProofDialogOpen(true);
                          }}
                          data-testid={`view-proof-${payment.id}`}
                        >
                          <Eye className="w-3 h-3" />
                          Lihat
                        </Button>
                      ) : payment.status === 'belum_bayar' && !isPemilik() ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setUploadDialogOpen(true);
                          }}
                          data-testid={`upload-proof-${payment.id}`}
                        >
                          <Upload className="w-3 h-3" />
                          Upload
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`payment-menu-${payment.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!isPemilik() && payment.status === 'belum_bayar' && (
                            <DropdownMenuItem onClick={() => { setSelectedPayment(payment); setUploadDialogOpen(true); }}>
                              <Upload className="w-4 h-4 mr-2" />Upload Bukti Transfer
                            </DropdownMenuItem>
                          )}
                          {payment.proof_image_url && (
                            <DropdownMenuItem onClick={() => { setSelectedPayment(payment); setViewProofDialogOpen(true); }}>
                              <Eye className="w-4 h-4 mr-2" />Lihat Bukti Transfer
                            </DropdownMenuItem>
                          )}
                          {isPemilik() && payment.status === 'menunggu_verifikasi' && (
                            <DropdownMenuItem onClick={() => { setSelectedPayment(payment); setViewProofDialogOpen(true); }} className="text-green-600">
                              <CheckCircle className="w-4 h-4 mr-2" />Verifikasi Pembayaran
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handlePrintReceipt(payment)} data-testid={`print-receipt-${payment.id}`}>
                            <Printer className="w-4 h-4 mr-2" />Cetak Kwitansi
                          </DropdownMenuItem>
                          {isPemilik() && (
                            <>
                              {payment.status === 'belum_bayar' && (
                                <DropdownMenuItem onClick={() => handleMarkAsPaid(payment)} data-testid={`confirm-payment-${payment.id}`}>
                                  <CheckCircle className="w-4 h-4 mr-2" />Konfirmasi Manual
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDelete(payment)} className="text-destructive" data-testid={`delete-payment-${payment.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" />Hapus
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={setPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </Card>
        </>
      )}

      {/* Create Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Buat Tagihan Baru</DialogTitle>
            <DialogDescription>
              Buat tagihan pembayaran untuk penghuni
            </DialogDescription>
          </DialogHeader>
          <PaymentForm
            tenants={tenants}
            onSave={handleSave}
            onClose={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Confirm Payment Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Pembayaran?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin mengonfirmasi pembayaran {formatCurrency(selectedPayment?.amount)} dari {selectedPayment?.tenant_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMarkAsPaid}
              className="rounded-xl"
              data-testid="confirm-payment-action"
            >
              Konfirmasi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Tagihan?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus tagihan ini? Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-delete-payment"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Kwitansi Pembayaran
            </DialogTitle>
            <DialogDescription>
              Preview kwitansi sebelum mencetak
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            <Receipt ref={receiptRef} receipt={receiptData} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setReceiptDialogOpen(false)} className="rounded-xl">
              Tutup
            </Button>
            <Button onClick={printReceipt} className="rounded-xl" data-testid="print-receipt-btn">
              <Printer className="w-4 h-4 mr-2" />
              Cetak
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Proof Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
        setUploadDialogOpen(open);
        if (!open) {
          setProofFile(null);
          setProofPreview(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Bukti Transfer
            </DialogTitle>
            <DialogDescription>
              Upload foto bukti transfer untuk tagihan {selectedPayment && formatMonth(selectedPayment.month)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Amount Info */}
            {selectedPayment && (
              <Card className="p-4 bg-muted/50">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Jumlah yang harus dibayar:</span>
                  <span className="text-xl font-bold text-primary">{formatCurrency(selectedPayment.amount)}</span>
                </div>
              </Card>
            )}

            {/* File Input */}
            <div className="space-y-2">
              <Label>Foto Bukti Transfer</Label>
              <div className="border-2 border-dashed rounded-xl p-6 text-center hover:border-primary transition-colors">
                {proofPreview ? (
                  <div className="space-y-3">
                    <img 
                      src={proofPreview} 
                      alt="Preview" 
                      className="max-h-48 mx-auto rounded-lg object-contain"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setProofFile(null);
                        setProofPreview(null);
                      }}
                      className="rounded-xl"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Hapus
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <Image className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-1">Klik untuk pilih gambar</p>
                    <p className="text-xs text-muted-foreground">JPG, PNG, WebP (max 5MB)</p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="proof-file-input"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Bank Account Reminder */}
            {bankAccounts.length > 0 && (
              <Card className="p-3 bg-blue-50 border-blue-200">
                <p className="text-xs text-blue-800 font-medium mb-2">Transfer ke:</p>
                {bankAccounts.slice(0, 2).map((acc) => (
                  <div key={acc.id} className="text-xs text-blue-700">
                    {acc.bank_name}: <span className="font-mono font-bold">{acc.account_number}</span> (a.n. {acc.account_holder})
                  </div>
                ))}
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setUploadDialogOpen(false);
                setProofFile(null);
                setProofPreview(null);
              }} 
              className="rounded-xl"
            >
              Batal
            </Button>
            <Button 
              onClick={handleUploadProof} 
              disabled={!proofFile || uploadLoading}
              className="rounded-xl"
              data-testid="submit-proof-btn"
            >
              {uploadLoading ? <div className="w-5 h-5 spinner" /> : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Proof Dialog */}
      <Dialog open={viewProofDialogOpen} onOpenChange={setViewProofDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Image className="w-5 h-5" />
              Bukti Transfer
            </DialogTitle>
            <DialogDescription>
              {selectedPayment && `${selectedPayment.tenant_name} - ${formatMonth(selectedPayment.month)}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Payment Info */}
            {selectedPayment && (
              <Card className="p-4 bg-muted/50">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Jumlah:</span>
                    <p className="font-bold">{formatCurrency(selectedPayment.amount)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <p>
                      <Badge className={
                        selectedPayment.status === 'lunas' ? 'bg-green-100 text-green-700' :
                        selectedPayment.status === 'menunggu_verifikasi' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {getStatusLabel(selectedPayment.status)}
                      </Badge>
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Proof Image */}
            {selectedPayment?.proof_image_url && (
              <div className="border rounded-lg overflow-hidden">
                <img 
                  src={selectedPayment.proof_image_url.startsWith('http') ? selectedPayment.proof_image_url : `${API_URL}${selectedPayment.proof_image_url}`}
                  alt="Bukti Transfer" 
                  className="w-full object-contain max-h-96"
                />
              </div>
            )}

            {/* Upload time */}
            {selectedPayment?.proof_uploaded_at && (
              <p className="text-xs text-muted-foreground text-center">
                Diupload pada: {new Date(selectedPayment.proof_uploaded_at).toLocaleString('id-ID')}
              </p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setViewProofDialogOpen(false)} 
              className="rounded-xl"
            >
              Tutup
            </Button>
            
            {/* Pemilik actions for pending verification */}
            {isPemilik() && selectedPayment?.status === 'menunggu_verifikasi' && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setRejectDialogOpen(true)}
                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Tolak
                </Button>
                <Button 
                  onClick={handleVerifyPayment}
                  disabled={verifyLoading}
                  className="rounded-xl bg-green-600 hover:bg-green-700"
                  data-testid="verify-payment-btn"
                >
                  {verifyLoading ? <div className="w-5 h-5 spinner" /> : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verifikasi & Tandai Lunas
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Proof Confirmation */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tolak Bukti Transfer?</AlertDialogTitle>
            <AlertDialogDescription>
              Bukti transfer akan ditolak dan penghuni akan diminta untuk mengupload ulang bukti yang valid. Penghuni akan menerima notifikasi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRejectProof}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-reject-proof"
            >
              Tolak Bukti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Pembayaran;
